/**
 * src/components/notifications/PushPermissionBanner.tsx
 * Small, elegant floating popup prompting user to enable push notifications when disabled.
 * Displays every time the user opens the app until permission is granted.
 */

import React, { useState } from 'react';
import { Bell, BellOff, X, Sparkles, Check, ChevronRight } from 'lucide-react';
import { useNotifications } from '../../context/NotificationContext';
import { haptic, playTapSound } from '../../utils/audioHaptics';

export const PushPermissionBanner: React.FC = () => {
  const { permission, requestPermission } = useNotifications();
  const [isDismissedThisSession, setIsDismissedThisSession] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem('ckaudit_push_prompt_dismissed') === 'true';
  });
  const [isRequesting, setIsRequesting] = useState(false);
  const [showDeniedTip, setShowDeniedTip] = useState(false);

  // If already granted, or dismissed in this open session, do not render
  if (permission === 'granted' || isDismissedThisSession) {
    return null;
  }

  const handleEnable = async () => {
    haptic.tap();
    playTapSound();
    setIsRequesting(true);
    try {
      if (permission === 'denied') {
        setShowDeniedTip(true);
      } else {
        await requestPermission();
      }
    } finally {
      setIsRequesting(false);
    }
  };

  const handleDismiss = () => {
    haptic.tap();
    playTapSound();
    setIsDismissedThisSession(true);
    try {
      sessionStorage.setItem('ckaudit_push_prompt_dismissed', 'true');
    } catch {}
  };

  return (
    <div
      style={{
        position: 'fixed',
        bottom: '88px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: 'calc(100% - 28px)',
        maxWidth: '430px',
        zIndex: 9999,
        animation: 'slideUpSmooth 0.35s cubic-bezier(0.16, 1, 0.3, 1)',
        pointerEvents: 'auto'
      }}
    >
      <div
        className="apple-card"
        style={{
          padding: '12px 14px',
          borderRadius: '18px',
          background: 'var(--surface-glass)',
          backdropFilter: 'blur(30px) saturate(190%)',
          WebkitBackdropFilter: 'blur(30px) saturate(190%)',
          border: '1px solid rgba(255, 159, 10, 0.35)',
          boxShadow: '0 12px 36px rgba(0, 0, 0, 0.38), 0 0 16px rgba(255, 159, 10, 0.15)',
          display: 'flex',
          flexDirection: 'column',
          gap: '8px'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
          {/* Glowing Bell Icon */}
          <div
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '11px',
              background: 'linear-gradient(135deg, rgba(255, 159, 10, 0.25) 0%, rgba(255, 69, 58, 0.2) 100%)',
              border: '1px solid rgba(255, 159, 10, 0.4)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
              color: 'var(--ios-orange)'
            }}
          >
            <Bell size={18} strokeWidth={2.4} style={{ animation: 'bellSwing 2s infinite ease-in-out' }} />
          </div>

          {/* Copy */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{
              fontSize: '0.86rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              letterSpacing: '-0.01em',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}>
              <span>Enable Push Notifications</span>
              <span style={{
                fontSize: '0.625rem',
                fontWeight: 800,
                padding: '1px 6px',
                borderRadius: '6px',
                background: 'rgba(255, 159, 10, 0.18)',
                color: 'var(--ios-orange)'
              }}>
                OFF
              </span>
            </div>
            <div style={{
              fontSize: '0.72rem',
              color: 'var(--text-secondary)',
              marginTop: '1px',
              lineHeight: 1.3
            }}>
              {permission === 'denied'
                ? 'Notifications are blocked in browser.'
                : 'Get instant alerts for expenses, bills & approvals.'}
            </div>
          </div>

          {/* Enable CTA Button */}
          <button
            type="button"
            onClick={handleEnable}
            disabled={isRequesting}
            className="ios-tap-target"
            style={{
              padding: '6px 14px',
              borderRadius: 'var(--radius-pill)',
              background: 'linear-gradient(135deg, var(--ios-blue) 0%, #0066cc 100%)',
              color: '#ffffff',
              border: 'none',
              fontSize: '0.78rem',
              fontWeight: 700,
              cursor: 'pointer',
              flexShrink: 0,
              boxShadow: '0 2px 8px rgba(10, 132, 255, 0.4)',
              display: 'flex',
              alignItems: 'center',
              gap: '4px'
            }}
          >
            <span>{isRequesting ? 'Enabling...' : permission === 'denied' ? 'Settings' : 'Enable'}</span>
            {!isRequesting && <ChevronRight size={13} strokeWidth={2.6} />}
          </button>

          {/* Dismiss Icon */}
          <button
            type="button"
            onClick={handleDismiss}
            aria-label="Dismiss Notification Prompt"
            style={{
              background: 'transparent',
              border: 'none',
              color: 'var(--text-tertiary)',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer'
            }}
          >
            <X size={15} />
          </button>
        </div>

        {/* Tip for Denied Browser Permission */}
        {showDeniedTip && (
          <div style={{
            fontSize: '0.72rem',
            color: 'var(--text-secondary)',
            background: 'var(--surface-glass)',
            padding: '6px 10px',
            borderRadius: '10px',
            border: '1px solid var(--border-hairline)'
          }}>
            💡 <strong>To Enable:</strong> Tap the padlock or settings icon (🔒) beside the website URL at the top of your browser, then set <strong>Notifications</strong> to <strong>Allow</strong>.
          </div>
        )}
      </div>
    </div>
  );
};
